import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  Lightbulb,
  ListChecks,
  Loader2,
  MessageCircle,
  MessageSquare,
  Phone,
  Send,
  X,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from 'components/ui/button';
import { CompanionAvatar, defaultCompanion } from 'components/Companion';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/ui/tooltip';
import { useResponsiveLayout } from 'hooks/useResponsiveLayout';
import { useHoldPullToRefresh } from 'mobile/pullToRefreshHold';
import type { Question, ResponsePayload } from '../../types';
import {
  flowCompanion,
  type FlowCompanionMessage,
  type FlowCompanionSession,
} from '../../services/api/flowCompanionClient';

type FlowCompanionProps = {
  flowName: string;
  flowRunId: number;
  currentQuestion: Question;
  currentResponse?: ResponsePayload;
};

const SUGGESTED_QUESTIONS = [
  {
    icon: MessageSquare,
    label: "Summarize what I've said so far",
    prompt: "Summarize what I've said so far",
  },
  {
    icon: Lightbulb,
    label: 'Help me understand the question',
    prompt: 'What is this question asking?',
  },
  {
    icon: ListChecks,
    label: 'What should I focus on?',
    prompt: 'Clarify this material',
  },
] as const;

const TEMP_MESSAGE_ID_PREFIX = 'temp-user-';
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'textarea:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

type CrisisMetadata = {
  resourcesPath: string;
  ctaLabel: string;
};

type FailedAction =
  | { type: 'load' }
  | { type: 'send'; message: string };

const getMessageResourceMetadata = (
  message: FlowCompanionMessage,
  uiMode: 'crisis' | 'supportive_distress',
): CrisisMetadata | null => {
  const metadata = message.metadata ?? {};
  const resourcesPath = metadata.resources_path;
  const ctaLabel = metadata.cta_label;

  if (
    metadata.ui_mode === uiMode &&
    typeof resourcesPath === 'string' &&
    resourcesPath &&
    typeof ctaLabel === 'string' &&
    ctaLabel
  ) {
    return { resourcesPath, ctaLabel };
  }

  return null;
};

export const FlowCompanion: React.FC<FlowCompanionProps> = ({
  flowName,
  flowRunId,
  currentQuestion,
  currentResponse,
}) => {
  const { isMobile } = useResponsiveLayout();
  const [isOpen, setIsOpen] = useState(false);
  // The open panel is a full-screen conversation; a reload would close it mid-thread.
  useHoldPullToRefresh(isOpen);
  const [session, setSession] = useState<FlowCompanionSession | null>(null);
  const [messages, setMessages] = useState<FlowCompanionMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [failedAction, setFailedAction] = useState<FailedAction | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [dismissedSupportiveMessageId, setDismissedSupportiveMessageId] = useState<number | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const openerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const wasOpenRef = useRef(false);
  const latestAssistantMessage = [...messages].reverse().find((message) => !message.is_user) ?? null;
  const crisisMetadata = latestAssistantMessage
    ? getMessageResourceMetadata(latestAssistantMessage, 'crisis')
    : null;
  const supportiveMetadata = latestAssistantMessage
    ? getMessageResourceMetadata(latestAssistantMessage, 'supportive_distress')
    : null;
  const isCrisisMode = Boolean(crisisMetadata);
  const companionMood = supportiveMetadata ? 'gentle'
    : isLoading || isSending ? 'thinking'
      : isOpen ? 'listening' : 'idle';
  const shouldShowSupportiveBanner = Boolean(
    supportiveMetadata &&
      latestAssistantMessage &&
      latestAssistantMessage.id !== dismissedSupportiveMessageId &&
      !isCrisisMode,
  );

  const closeCompanion = useCallback(() => {
    setIsOpen(false);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setSession(null);
    setMessages([]);
    setInputValue('');
    setError(null);
    setFailedAction(null);
    setIsLoading(false);
    setIsSending(false);
    setDismissedSupportiveMessageId(null);
  }, [flowName, flowRunId]);

  useEffect(() => {
    if (!latestAssistantMessage || !supportiveMetadata) {
      return;
    }
    setDismissedSupportiveMessageId((previous) =>
      previous === latestAssistantMessage.id ? previous : null,
    );
  }, [latestAssistantMessage, supportiveMetadata]);

  useEffect(() => {
    if (!isOpen) {
      if (wasOpenRef.current) {
        wasOpenRef.current = false;
        openerRef.current?.focus();
      }
      return;
    }

    wasOpenRef.current = true;
    (closeButtonRef.current ?? panelRef.current)?.focus();

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeCompanion();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [closeCompanion, isOpen]);

  const ensureSessionLoaded = useCallback(async (reloadMessages = false) => {
    if (session && !reloadMessages) {
      return session;
    }

    setIsLoading(true);
    setError(null);
    setFailedAction(null);
    try {
      let nextSession = session;
      if (!nextSession) {
        const sessionResponse = await flowCompanion.createSession(flowName, flowRunId);
        nextSession = sessionResponse.data;
        setSession(nextSession);
      }

      const messagesResponse = await flowCompanion.listMessages(flowName, nextSession.id);
      setMessages(messagesResponse.data);
      return nextSession;
    } catch {
      setError("We couldn't open the companion. Please try again.");
      setFailedAction({ type: 'load' });
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [flowName, flowRunId, session]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    void ensureSessionLoaded();
  }, [ensureSessionLoaded, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    messagesEndRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, [isOpen, messages, isSending]);

  const getCurrentResponseContext = useCallback(() => {
    if (!currentResponse) {
      return undefined;
    }

    const payload: Record<string, unknown> = {};

    if (typeof currentResponse.text_value === 'string' && currentResponse.text_value.trim()) {
      payload.text_value = currentResponse.text_value;
    }
    if (Array.isArray(currentResponse.text_list) && currentResponse.text_list.length > 0) {
      payload.text_list = currentResponse.text_list;
    }
    if (typeof currentResponse.boolean_value === 'boolean') {
      payload.boolean_value = currentResponse.boolean_value;
    }
    if (typeof currentResponse.rating_value === 'number') {
      payload.rating_value = currentResponse.rating_value;
    }
    if (
      typeof currentResponse.custom_other_value === 'string' &&
      currentResponse.custom_other_value.trim()
    ) {
      payload.custom_other_value = currentResponse.custom_other_value;
    }
    if (typeof currentResponse.audio_url === 'string' && currentResponse.audio_url.trim()) {
      payload.audio_url = currentResponse.audio_url;
    }
    if (
      Array.isArray(currentResponse.selected_option_details) &&
      currentResponse.selected_option_details.length > 0
    ) {
      payload.selected_option_details = currentResponse.selected_option_details.map((option) => ({
        id: option.id,
        front_text: option.front_text ?? null,
        back_text: option.back_text ?? null,
      }));
    }

    return Object.keys(payload).length > 0 ? payload : undefined;
  }, [currentResponse]);

  const submitMessage = useCallback(
    async (messageText: string) => {
      const trimmed = messageText.trim();
      if (!trimmed || isSending || isCrisisMode) {
        return;
      }

      setIsSending(true);
      setError(null);
      setFailedAction(null);

      const optimisticId = `${TEMP_MESSAGE_ID_PREFIX}${Date.now()}`;
      const optimisticMessage: FlowCompanionMessage = {
        id: Number.MIN_SAFE_INTEGER,
        content: trimmed,
        is_user: true,
        created_at: new Date().toISOString(),
        question_id: currentQuestion.id ?? null,
        metadata: {
          tempId: optimisticId,
        },
      };

      let activeSession = session;
      try {
        if (!activeSession) {
          activeSession = await ensureSessionLoaded();
        }
        if (!activeSession) {
          throw new Error('Unable to start the question helper right now.');
        }

        setMessages((previous) => [...previous, optimisticMessage]);
        setInputValue('');

        const response = await flowCompanion.sendMessage(flowName, activeSession.id, {
          message: trimmed,
          current_question_id: currentQuestion.id ?? null,
          current_response: getCurrentResponseContext(),
        });
        setMessages((previous) => [
          ...previous.filter((message) => message !== optimisticMessage),
          optimisticMessage,
          response.data,
        ]);
        setSession((previous) =>
          previous
            ? {
                ...previous,
                turn_count: previous.turn_count + 1,
                last_question_id: currentQuestion.id ?? null,
              }
            : previous,
        );
      } catch {
        setMessages((previous) => previous.filter((message) => message !== optimisticMessage));
        setInputValue(trimmed);
        setError("We couldn't send that message. Your question is still here so you can try again.");
        setFailedAction({ type: 'send', message: trimmed });
      } finally {
        setIsSending(false);
      }
    },
    [
      currentQuestion.id,
      ensureSessionLoaded,
      flowName,
      getCurrentResponseContext,
      isCrisisMode,
      isSending,
      session,
    ],
  );

  const handleSuggestionClick = useCallback(
    (prompt: string) => {
      void submitMessage(prompt);
    },
    [submitMessage],
  );

  const handleRetry = useCallback(() => {
    if (!failedAction || isLoading || isSending) {
      return;
    }

    if (failedAction.type === 'load') {
      void ensureSessionLoaded(true);
      return;
    }

    void submitMessage(failedAction.message);
  }, [ensureSessionLoaded, failedAction, isLoading, isSending, submitMessage]);

  const handlePanelKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab' || !panelRef.current) {
      return;
    }

    const focusableElements = Array.from(
      panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
    ).filter((element) => element.getAttribute('aria-hidden') !== 'true');

    if (focusableElements.length === 0) {
      event.preventDefault();
      panelRef.current.focus();
      return;
    }

    const first = focusableElements[0];
    const last = focusableElements[focusableElements.length - 1];
    const activeElement = document.activeElement;

    if (event.shiftKey && (activeElement === first || !panelRef.current.contains(activeElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }, []);

  const panel = (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="flow-companion-title"
      tabIndex={-1}
      onKeyDown={handlePanelKeyDown}
      className={`fixed top-0 right-0 z-[470] flex h-full w-full max-w-md flex-col overflow-hidden bg-[#fcfaf4] text-slate-900 shadow-2xl transition-transform duration-300 ease-out ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      } ${isMobile ? 'max-w-full' : ''}`}
    >
      <div className="flex items-center justify-between border-b border-[#ddd6c8] px-4 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center">
            {isCrisisMode ? <MessageCircle className="h-5 w-5 text-slate-600" aria-hidden="true" />
              : <CompanionAvatar size={24} mood={companionMood} />}
          </div>
          <div>
            <h2 id="flow-companion-title" className="font-semibold text-slate-900">
              {isCrisisMode ? 'Companion' : defaultCompanion.displayName}
            </h2>
            <p className="text-xs text-slate-500">Here to help you learn</p>
          </div>
        </div>
        <Button
          ref={closeButtonRef}
          variant="ghost"
          size="icon"
          onClick={closeCompanion}
          aria-label="Close companion"
          className="text-slate-600 hover:bg-[#efe8da]"
        >
          <X className="h-5 w-5" />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        {isLoading ? (
          <div className="flex h-full items-center justify-center text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="space-y-4">
            <div className="py-8 text-center">
              <div className="mx-auto mb-4 flex h-10 w-10 items-center justify-center">
                <CompanionAvatar size={32} />
              </div>
              <h3 className="mb-2 font-medium text-slate-900">How can I help?</h3>
              <p className="mx-auto max-w-xs text-sm text-slate-500">
                Uses the current series of questions for context and may be wrong. Ask about the
                question or try one of the suggestions below.
              </p>
            </div>

            <div className="space-y-2">
              {SUGGESTED_QUESTIONS.map((question) => {
                const Icon = question.icon;
                return (
                  <button
                    key={question.label}
                    type="button"
                    onClick={() => handleSuggestionClick(question.prompt)}
                    disabled={isLoading || isSending || isCrisisMode}
                    className="flex w-full items-center gap-3 rounded-lg bg-[#efe8da] p-3 text-left transition-colors hover:bg-[#e7dfcf] disabled:opacity-50"
                  >
                    <Icon className="h-5 w-5 shrink-0 text-foundation-base-darkbase-dark-500" />
                    <span className="text-sm text-slate-900">{question.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((message, index) => {
              const messageCrisisMetadata = !message.is_user
                ? getMessageResourceMetadata(message, 'crisis')
                : null;

              if (messageCrisisMetadata) {
                return (
                  <div key={`${message.id}-${index}`} className="flex justify-start">
                    <div className="max-w-[92%] rounded-3xl border border-[#f2b7ac] bg-[#fff2ef] px-4 py-4 text-slate-900 shadow-sm">
                      <div className="mb-3 flex items-start gap-3">
                        <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-full bg-[#f8d4cd] text-[#9f2d20]">
                          <AlertTriangle className="h-5 w-5" />
                        </div>
                        <div className="space-y-1">
                          <p className="text-sm font-semibold text-[#7f2116]">
                            Immediate support recommended
                          </p>
                          <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-800">
                            {message.content}
                          </p>
                        </div>
                      </div>
                      <div className="rounded-2xl bg-white/80 px-4 py-3 text-sm text-slate-800">
                        <div className="flex items-center gap-2 font-medium text-[#7f2116]">
                          <Phone className="h-4 w-4" />
                          <span>Call or text 988 now</span>
                        </div>
                        <p className="mt-1 leading-relaxed">
                          If you may act on these thoughts or are in immediate danger, call 911
                          right now.
                        </p>
                      </div>
                      <div className="mt-4 flex flex-wrap gap-3">
                        <Button asChild className="w-auto px-5 py-3">
                          <Link to={messageCrisisMetadata.resourcesPath}>
                            {messageCrisisMetadata.ctaLabel}
                          </Link>
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          size="auto"
                          className="w-auto"
                          onClick={closeCompanion}
                        >
                          Close Companion
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <div
                  key={`${message.id}-${index}`}
                  className={`flex gap-2 ${message.is_user ? 'justify-end' : 'justify-start'}`}
                >
                  {!message.is_user && !isCrisisMode && <CompanionAvatar size={20} mood={supportiveMetadata ? 'gentle' : 'idle'} className="mt-3" />}
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                      message.is_user
                        ? 'bg-foundation-base-darkbase-dark-500 text-white'
                        : 'bg-[#efe8da] text-slate-900'
                    }`}
                  >
                    <p className="whitespace-pre-wrap">{message.content}</p>
                  </div>
                </div>
              );
            })}
            {isSending && messages[messages.length - 1]?.is_user && !isCrisisMode ? (
              <div className="flex justify-start">
                <div className="rounded-2xl bg-[#efe8da] px-4 py-3">
                  <div className="flex gap-1">
                    <span
                      className="h-2 w-2 animate-bounce rounded-full bg-slate-400"
                      style={{ animationDelay: '0ms' }}
                    />
                    <span
                      className="h-2 w-2 animate-bounce rounded-full bg-slate-400"
                      style={{ animationDelay: '150ms' }}
                    />
                    <span
                      className="h-2 w-2 animate-bounce rounded-full bg-slate-400"
                      style={{ animationDelay: '300ms' }}
                    />
                  </div>
                </div>
              </div>
            ) : null}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      <div className="border-t border-[#ddd6c8] px-4 py-4">
        {error ? (
          <div
            role="alert"
            className="mb-3 flex items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
          >
            <span>{error}</span>
            {failedAction ? (
              <Button
                type="button"
                variant="secondary"
                size="auto"
                onClick={handleRetry}
                disabled={isLoading || isSending}
              >
                Retry
              </Button>
            ) : null}
          </div>
        ) : null}

        {isCrisisMode && crisisMetadata ? (
          <div className="space-y-3 rounded-2xl border border-[#f2b7ac] bg-[#fff6f3] px-4 py-4">
            <p className="text-sm font-medium text-[#7f2116]">
              The companion is paused so you can focus on immediate support.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button asChild className="w-auto px-5 py-3">
                <Link to={crisisMetadata.resourcesPath}>{crisisMetadata.ctaLabel}</Link>
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="auto"
                className="w-auto"
                onClick={closeCompanion}
              >
                Close Companion
              </Button>
            </div>
          </div>
        ) : (
          <>
            {shouldShowSupportiveBanner && supportiveMetadata && latestAssistantMessage ? (
              <div className="mb-3 rounded-2xl border border-[#e7d4a5] bg-[#fff8e8] px-4 py-4 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-full bg-[#f6e6b6] text-[#8a6411]">
                      <Lightbulb className="h-5 w-5" />
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-semibold text-[#7a5a14]">Extra support is available</p>
                      <p className="text-sm leading-relaxed text-slate-800">
                        If you are going through a hard time, you can open support and emergency
                        resources while continuing this conversation.
                      </p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label="Dismiss support resources banner"
                    className="h-8 w-8 shrink-0 text-[#8a6411] hover:bg-[#f5ebcc]"
                    onClick={() => setDismissedSupportiveMessageId(latestAssistantMessage.id)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <div className="mt-3">
                  <Button asChild className="w-auto px-5 py-3">
                    <Link to={supportiveMetadata.resourcesPath}>{supportiveMetadata.ctaLabel}</Link>
                  </Button>
                </div>
              </div>
            ) : null}
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void submitMessage(inputValue);
            }}
            className="space-y-2"
          >
            <input
              type="text"
              value={inputValue}
              onChange={(event) => setInputValue(event.target.value)}
              placeholder="Ask me anything..."
              disabled={isLoading || isSending}
              className="w-full rounded-xl border border-transparent bg-[#efe8da] px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-foundation-base-darkbase-dark-500"
            />
            <Button
              type="submit"
              size="icon"
              aria-label="Send message"
              disabled={isLoading || isSending || !inputValue.trim()}
              className="ml-auto flex h-12 w-12 min-h-0 rounded-xl bg-foundation-base-darkbase-dark-500 p-0 text-white hover:bg-foundation-base-darkbase-dark-700"
            >
              {isSending ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Send className="h-5 w-5" />
              )}
            </Button>
          </form>
          </>
        )}
      </div>
    </div>
  );

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            ref={openerRef}
            type="button"
            className="z-[400] flex h-12 w-12 items-center justify-center rounded-full border border-solid border-white/10 bg-white/40 backdrop-blur-sm backdrop-brightness-[110%] shadow-elevation-sm transition-colors hover:bg-white [-webkit-backdrop-filter:blur(4px)_brightness(100%)]"
            onClick={() => setIsOpen(true)}
            aria-label="Open companion"
            data-tour-id="question-flow-control-companion"
          >
            <MessageCircle
              className="h-6 w-6 text-foundation-base-darkbase-dark-500"
              strokeWidth={1}
              aria-hidden="true"
            />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top">{isCrisisMode ? 'Open companion' : `Ask ${defaultCompanion.displayName}`}</TooltipContent>
      </Tooltip>

      {isOpen && (
        <div
          className="fixed inset-0 z-[460] bg-foreground/20 backdrop-blur-sm md:hidden"
          onClick={closeCompanion}
        />
      )}

      {isOpen ? panel : null}
    </>
  );
};
